import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, Clock3, MapPin, Navigation, Star, Store } from 'lucide-react'
import "./TopVendorsOne.css";
import { VendorGridSkeleton } from "./SkeletonLoader";

const FALLBACK_LOGO = "assets/images/thumbs/vendor-logo1.png";
const FALLBACK_COVER = "assets/images/thumbs/store-img2.png";
const INVALID_ASSET_VALUES = ["null", "undefined", "false", "0", "n/a", "na"];

const getStoreItems = (stores) => {
    const candidates = [
        stores?.cell_contents,
        stores?.response_data?.[0]?.cell_contents,
        stores?.response_data,
        stores?.data?.cell_contents,
        stores?.data?.response_data?.[0]?.cell_contents,
        stores?.data?.response_data,
        stores?.data,
        stores,
    ];

    return candidates.find((item) => Array.isArray(item)) || [];
};

const TopVendors = ({
    stores,
    loading = false,
    selectedStoreId = "",
    onStoreSelect,
    emptyMessage = "No stores available for this location.",
    searchTerm = "",
    segment,
    segmentId: selectedSegmentId = "",
}) => {
    const storeItems = getStoreItems(stores);

    const getValue = (item, keys) => {
        const matchedKey = keys.find(
            (key) => item?.[key] !== null && item?.[key] !== undefined && item?.[key] !== ""
        );

        return matchedKey ? item[matchedKey] : "";
    };

    const getAssetValue = (item, keys, fallback) => {
        const value = String(getValue(item, keys) || "").trim();

        if (!value || INVALID_ASSET_VALUES.includes(value.toLowerCase())) {
            return fallback;
        }

        return value;
    };

    const getStoreId = (item) =>
        getValue(item, [
            "business_segment_id",
            "businessSegmentId",
            "store_id",
            "storeId",
            "id",
            "branch_id",
        ]);

    const getSegmentId = (item) =>
        getValue(item, ["segment_id", "segmentId", "segment"]);

    const getStoreTitle = (item, index) =>
        getValue(item, ["title", "name", "store_name", "storeName", "business_name", "businessName"]) ||
        `Store ${index + 1}`;

    const getStoreLogo = (item) =>
        getAssetValue(item, ["image", "logo", "store_logo", "storeLogo", "icon"], FALLBACK_LOGO);

    const getStoreCover = (item) =>
        getAssetValue(item, [
            "background_image",
            "backgroundImage",
            "cover_image",
            "coverImage",
            "banner_image",
            "bannerImage",
            "store_image",
            "storeImage",
            "image",
        ], FALLBACK_COVER);

    const getStoreRating = (item) => {
        const rating = getValue(item, ["rating", "avg_rating", "store_rating", "storeRating"]);
        const ratingNumber = Number(rating);

        return Number.isFinite(ratingNumber) && ratingNumber > 0
            ? ratingNumber.toFixed(1)
            : "4.5";
    };

    const getDeliveryLabel = (item) => {
        const deliveryTime = getValue(item, [
            "time",
            "delivery_time",
            "deliveryTime",
            "estimated_delivery_time",
            "estimatedDeliveryTime",
        ]);

        if (!deliveryTime) return "Delivery in 30-45 min";

        const label = String(deliveryTime).trim();
        return /delivery/i.test(label) ? label : `Delivery in ${label}`;
    };

    const getDistanceLabel = (item) =>
        getValue(item, ["distance", "store_distance", "storeDistance", "distance_text", "distanceText"]) ||
        "Nearby";

    const getStoreAddress = (item) =>
        getValue(item, ["address", "store_address", "storeAddress", "location", "area_name", "areaName"]) ||
        "Fresh picks and daily essentials";

    const getOfferText = (item) =>
        getValue(item, [
            "offer_text",
            "offerText",
            "discount_text",
            "discountText",
            "coupon",
            "offer",
            "promo_text",
            "promoText",
        ]) || "Top Store";

    const getStoreStatus = (item) => {
        const statusValue = getValue(item, [
            "status_text",
            "statusText",
            "store_status",
            "storeStatus",
            "is_open",
            "isOpen",
            "open",
        ]);

        if (statusValue === false) return "Closed";

        const normalizedStatus = String(statusValue).trim().toLowerCase();
        if (!normalizedStatus) return "Open";
        if (["0", "false", "closed", "close", "offline", "inactive"].includes(normalizedStatus)) {
            return "Closed";
        }
        if (["1", "true", "open", "opened", "online", "active"].includes(normalizedStatus)) {
            return "Open";
        }

        return String(statusValue).trim();
    };

    const handleImageError = (fallbackImage) => (event) => {
        event.currentTarget.onerror = null;
        event.currentTarget.src = fallbackImage;
    };

    const getStoreSlug = (item) => {
        const slugValue =
            getValue(item, ["slug", "url_slug", "urlSlug", "segment_slug", "segmentSlug"]) ||
            getValue(item, ["segment_name", "segmentName", "category_name", "categoryName"]) ||
            getValue(item, ["store_name", "storeName", "business_name", "businessName"]) ||
            item?.title ||
            item?.name ||
            "grocery";

        return String(slugValue)
            .trim()
            .toLowerCase()
            .replace(/&/g, "and")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "") || "grocery";
    };

    const getStorePath = (item) => {
        const storeId = getStoreId(item);
        const storeSlug = getStoreSlug(item);
        return storeId
            ? `/category/${encodeURIComponent(`${storeSlug}-${storeId}`)}`
            : "/category/grocery";
    };

    const handleStoreClick = (event, item) => {
        if (!onStoreSelect) return;

        event.preventDefault();
        onStoreSelect(item);
    };

    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const displayedStores = storeItems.map((item, index) => ({ item, index })).filter(
        ({ item, index }) => String(getStoreTitle(item, index)).toLowerCase().includes(normalizedSearchTerm)
    );

    if (loading) {
        return <VendorGridSkeleton />;
    }

    if (!displayedStores.length) {
        return (
            <section className="top-vendors top-vendors-pro py-24">
                <div className="container container-lg">
                    <div className="top-vendors-empty" role="status">
                        <Store size={28} strokeWidth={2} />
                        <span>{normalizedSearchTerm ? "No stores found. Try another search." : emptyMessage}</span>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="top-vendors top-vendors-pro py-24">
            <div className="container container-lg">
                <div className="section-heading">
                    <h5 className="mb-0">{stores?.cell_title_text || "Top Stores"}</h5>
                </div>
                <div className="row vendor-card-wrapper top-vendor-grid">
                    {displayedStores.map(({ item, index: i }) => {
                        const storeId = getStoreId(item);
                        const segmentId = getSegmentId(item);
                        const title = getStoreTitle(item, i);
                        const logoImage = getStoreLogo(item);
                        const coverImage = getStoreCover(item);
                        const statusText = getStoreStatus(item);
                        const isOpen = !["closed", "offline", "inactive"].includes(
                            String(statusText).trim().toLowerCase()
                        );
                        const isSelected = String(storeId || "") === String(selectedStoreId || "");

                        return (
                        <div className="col-xxl-3 col-lg-3 col-sm-6" key={storeId || `${title}-${i}`}>
                            <Link
                                to={getStorePath(item)}
                                className={`top-vendor-card__link ${isSelected ? "is-selected" : ""}`}
                                onClick={(event) => handleStoreClick(event, item)}
                                state={{
                                    store: item,
                                    storeId,
                                    businessSegmentId: storeId,
                                    segmentId: segmentId || selectedSegmentId,
                                    segment,
                                }}
                            >
                                <article className="vendor-card top-vendor-card">
                                    <div className="top-vendor-card__media">
                                        <img
                                            src={coverImage}
                                            className="top-vendor-card__cover"
                                            alt={title}
                                            onError={handleImageError(FALLBACK_COVER)}
                                        />

                                        <span className="top-vendor-card__badge top-vendor-card__badge--offer">
                                            {getOfferText(item)}
                                        </span>

                                        <span
                                            className={`top-vendor-card__badge top-vendor-card__badge--status ${
                                                isOpen ? "is-open" : "is-closed"
                                            }`}
                                        >
                                            {statusText}
                                        </span>

                                        {isSelected && (
                                            <span className="top-vendor-card__selected-icon" aria-label="Selected">
                                                <i className="ph ph-check" aria-hidden="true" />
                                            </span>
                                        )}

                                        <span className="top-vendor-card__logo-wrap">
                                            {logoImage ? (
                                                <img
                                                    src={logoImage}
                                                    alt={`${title} logo`}
                                                    className="top-vendor-card__logo"
                                                    onError={handleImageError(FALLBACK_LOGO)}
                                                />
                                            ) : (
                                                <Store size={26} strokeWidth={2.1} />
                                            )}
                                        </span>
                                    </div>

                                    <div className="top-vendor-card__body">
                                        <div className="top-vendor-card__title-row">
                                            <h6 className="top-vendor-card__title">{title}</h6>
                                            <span className="top-vendor-card__rating">
                                                <Star size={14} fill="currentColor" strokeWidth={2.3} />
                                                {getStoreRating(item)}
                                            </span>
                                        </div>

                                        <p className="top-vendor-card__address">
                                            <MapPin size={15} strokeWidth={2.2} />
                                            <span>{getStoreAddress(item)}</span>
                                        </p>

                                        <div className="top-vendor-card__meta">
                                            <span>
                                                <Clock3 size={15} strokeWidth={2.2} />
                                                <span>{getDeliveryLabel(item)}</span>
                                            </span>
                                            <span>
                                                <Navigation size={15} strokeWidth={2.2} />
                                                <span>{getDistanceLabel(item)}</span>
                                            </span>
                                        </div>

                                        <div className="top-vendor-card__footer">
                                            <span>Explore Store</span>
                                            <span className="top-vendor-card__arrow" aria-hidden="true">
                                                <ArrowUpRight size={16} strokeWidth={2.3} />
                                            </span>
                                        </div>
                                    </div>
                                </article>
                            </Link>
                        </div>
                        );
                    })}
                   {/* <div className="col-xxl-3 col-lg-4 col-sm-6">
                        <div className="vendor-card text-center px-16 pb-24">
                            <div className="">
                                <img
                                    src="assets/images/thumbs/vendor-logo2.png"
                                    alt=""
                                    className="vendor-card__logo m-12"
                                />
                                <h6 className="title mt-32">Safeway</h6>
                                <span className="text-heading text-sm d-block">
                                    Delivery by 6:15am
                                </span>
                                <Link
                                    to="/shop"
                                    className="btn btn-main-two rounded-pill py-6 px-16 text-12 mt-8"
                                >
                                    $5 off Snack &amp; Candy
                                </Link>
                            </div>
                            <div className="vendor-card__list mt-22 flex-center flex-wrap gap-8">
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img1.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img2.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img3.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img4.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img5.png" alt="" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xxl-3 col-lg-4 col-sm-6">
                        <div className="vendor-card text-center px-16 pb-24">
                            <div className="">
                                <img
                                    src="assets/images/thumbs/vendor-logo3.png"
                                    alt=""
                                    className="vendor-card__logo m-12"
                                />
                                <h6 className="title mt-32">Food Max</h6>
                                <span className="text-heading text-sm d-block">
                                    Delivery by 6:15am
                                </span>
                                <Link
                                    to="/shop"
                                    className="btn btn-main-two rounded-pill py-6 px-16 text-12 mt-8"
                                >
                                    $5 off Snack &amp; Candy
                                </Link>
                            </div>
                            <div className="vendor-card__list mt-22 flex-center flex-wrap gap-8">
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img1.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img2.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img3.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img4.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img5.png" alt="" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xxl-3 col-lg-4 col-sm-6">
                        <div className="vendor-card text-center px-16 pb-24">
                            <div className="">
                                <img
                                    src="assets/images/thumbs/vendor-logo4.png"
                                    alt=""
                                    className="vendor-card__logo m-12"
                                />
                                <h6 className="title mt-32">HRmart</h6>
                                <span className="text-heading text-sm d-block">
                                    Delivery by 6:15am
                                </span>
                                <Link
                                    to="/shop"
                                    className="btn btn-main-two rounded-pill py-6 px-16 text-12 mt-8"
                                >
                                    $5 off Snack &amp; Candy
                                </Link>
                            </div>
                            <div className="vendor-card__list mt-22 flex-center flex-wrap gap-8">
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img1.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img2.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img3.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img4.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img5.png" alt="" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xxl-3 col-lg-4 col-sm-6">
                        <div className="vendor-card text-center px-16 pb-24">
                            <div className="">
                                <img
                                    src="assets/images/thumbs/vendor-logo5.png"
                                    alt=""
                                    className="vendor-card__logo m-12"
                                />
                                <h6 className="title mt-32">Lucky Supermarket</h6>
                                <span className="text-heading text-sm d-block">
                                    Delivery by 6:15am
                                </span>
                                <Link
                                    to="/shop"
                                    className="btn btn-main-two rounded-pill py-6 px-16 text-12 mt-8"
                                >
                                    $5 off Snack &amp; Candy
                                </Link>
                            </div>
                            <div className="vendor-card__list mt-22 flex-center flex-wrap gap-8">
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img1.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img2.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img3.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img4.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img5.png" alt="" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xxl-3 col-lg-4 col-sm-6">
                        <div className="vendor-card text-center px-16 pb-24">
                            <div className="">
                                <img
                                    src="assets/images/thumbs/vendor-logo6.png"
                                    alt=""
                                    className="vendor-card__logo m-12"
                                />
                                <h6 className="title mt-32">Arico Farmer</h6>
                                <span className="text-heading text-sm d-block">
                                    Delivery by 6:15am
                                </span>
                                <Link
                                    to="/shop"
                                    className="btn btn-main-two rounded-pill py-6 px-16 text-12 mt-8"
                                >
                                    $5 off Snack &amp; Candy
                                </Link>
                            </div>
                            <div className="vendor-card__list mt-22 flex-center flex-wrap gap-8">
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img1.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img2.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img3.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img4.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img5.png" alt="" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xxl-3 col-lg-4 col-sm-6">
                        <div className="vendor-card text-center px-16 pb-24">
                            <div className="">
                                <img
                                    src="assets/images/thumbs/vendor-logo7.png"
                                    alt=""
                                    className="vendor-card__logo m-12"
                                />
                                <h6 className="title mt-32">Farmer Market</h6>
                                <span className="text-heading text-sm d-block">
                                    Delivery by 6:15am
                                </span>
                                <Link
                                    to="/shop"
                                    className="btn btn-main-two rounded-pill py-6 px-16 text-12 mt-8"
                                >
                                    $5 off Snack &amp; Candy
                                </Link>
                            </div>
                            <div className="vendor-card__list mt-22 flex-center flex-wrap gap-8">
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img1.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img2.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img3.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img4.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img5.png" alt="" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xxl-3 col-lg-4 col-sm-6">
                        <div className="vendor-card text-center px-16 pb-24">
                            <div className="">
                                <img
                                    src="assets/images/thumbs/vendor-logo8.png"
                                    alt=""
                                    className="vendor-card__logo m-12"
                                />
                                <h6 className="title mt-32">Foodsco</h6>
                                <span className="text-heading text-sm d-block">
                                    Delivery by 6:15am
                                </span>
                                <Link
                                    to="/shop"
                                    className="btn btn-main-two rounded-pill py-6 px-16 text-12 mt-8"
                                >
                                    $5 off Snack &amp; Candy
                                </Link>
                            </div>
                            <div className="vendor-card__list mt-22 flex-center flex-wrap gap-8">
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img1.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img2.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img3.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img4.png" alt="" />
                                </div>
                                <div className="vendor-card__item bg-white rounded-circle flex-center">
                                    <img src="assets/images/thumbs/vendor-img5.png" alt="" />
                                </div>
                            </div>
                        </div>
                    </div>*/}
                </div>
                {/* <div className="home-section-action">
                    <Link
                        to="/stores"
                        className="home-section-action__link top-vendors-pro__view-all text-sm fw-medium"
                    >
                        All Stores
                    </Link>
                </div> */}
            </div>
        </section>

    )
}

export default TopVendors
